import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import {
  ArrowsUpDownIcon,
  DocumentDuplicateIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { exposureOptions } from 'data/constants';
import { AboveGradeWallType, SizingItemsType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';
import WallFenestrations from './wall-fenestrations';

type AboveGradeWallProps = {
  wall: AboveGradeWallType;
  updateFn: (wallId: string, updates: Partial<AboveGradeWallType>) => void;
  duplicateFn: (wallId: string) => void;
  deleteFn: (wallId: string) => void;
  switchGradeFn?: (wallId: string) => void;
  switchDisabledReason?: string;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
  defaultExpandAdvanced?: boolean;
  focusFenestrationId?: string;
};

export default function AboveGradeWallComponent({
  wall,
  updateFn,
  duplicateFn,
  deleteFn,
  switchGradeFn,
  switchDisabledReason,
  updateDefaultSizingFn,
  defaultExpandAdvanced = false,
  focusFenestrationId
}: AboveGradeWallProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.aboveGradeWall
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    defaultExpandAdvanced
  );
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const [switchQueued, setSwitchQueued] = useState(false);
  const sizingItemType = 'aboveGradeWalls';
  const childrenValid =
    (wall.verticalGlass || []).every((glass) => glass.valid) &&
    (wall.solidDoors || []).every((door) => door.valid);
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => wall[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (changes: Partial<AboveGradeWallType>) => {
    const newWall: AboveGradeWallType = {
      ...wall,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newWall);

    updateFn(wall.uniqueId, {
      ...newWall,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(wall.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(wall.uniqueId);
    setDeleteQueued(false);
  };

  const handleSwitchClick = () => {
    if (!switchQueued) {
      setSwitchQueued(true);
      return;
    }

    switchGradeFn?.(wall.uniqueId);
    setSwitchQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      aboveGradeWall: {
        ...wall,
        uniqueId: undefined,
        valid: undefined,
        width: undefined,
        height: undefined,
        exposure: undefined,
        verticalGlass: [],
        solidDoors: []
      }
    });
  };

  const classes = clsx(
    'flex flex-col gap-3 rounded-xl border-2 bg-gray-100',
    wall.valid && childrenValid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        key={wall.uniqueId}
        className='flex w-full flex-col divide-y divide-gray-300 rounded-lg bg-gray-200 px-5'
        ref={animate}
      >
        <div className='flex min-h-[54px] items-center gap-5'>
          <div className='font-semibold'>
            {wall.exposure} Wall (Above Grade)
          </div>
          <div className='w-[130px]'>
            <FormInput
              value={wall.width}
              prefix='Width: '
              placeholder='Width (ft)'
              type='number'
              suffix=' ft'
              min={1}
              onChange={(value) => handleUpdateWithValidation({ width: value })}
              required
            />
          </div>
          {!wall.valid && (
            <span className='text-red-500'>Invalid Attributes!</span>
          )}
          {!childrenValid && (
            <span className='text-red-500'>Glass/Doors Invalid!</span>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <button
              className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Advanced</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
              <Tooltip
                text={
                  showAdvancedOptions
                    ? 'Hide Advanced Options'
                    : 'Show Advanced Options'
                }
              />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDuplicateClick}
            >
              <DocumentDuplicateIcon className='h-5 w-5' />
              <Tooltip text='Duplicate Wall' />
            </button>
            {switchGradeFn && (
              <button
                className={clsx(
                  'relative h-full text-gray-500',
                  switchDisabledReason
                    ? 'cursor-not-allowed opacity-40'
                    : 'cursor-pointer hover:text-gray-700'
                )}
                type='button'
                disabled={!!switchDisabledReason}
                onClick={switchDisabledReason ? undefined : handleSwitchClick}
                onMouseLeave={() => setSwitchQueued(false)}
              >
                <ArrowsUpDownIcon className='h-5 w-5' />
                <Tooltip
                  text={
                    switchDisabledReason ||
                    (switchQueued
                      ? 'Click Again to Confirm (resets attributes)'
                      : 'Switch to Below Grade')
                  }
                />
              </button>
            )}
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={deleteQueued ? 'Click Again to Confirm' : 'Delete Wall'}
              />
            </button>
          </div>
        </div>
        {showAdvancedOptions && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
            <FormInput
              value={wall.exposure}
              options={exposureOptions}
              onChange={(value) =>
                handleUpdateWithValidation({ exposure: value })
              }
              label='Direction Facing'
              type='select'
              required
              preventDeselection
            />
            <div className='w-[80px]'>
              <FormInput
                value={wall.height}
                label='Height'
                placeholder='Height (ft)'
                subLabel='(ft)'
                type='number'
                suffix=' ft'
                min={1}
                onChange={(value) =>
                  handleUpdateWithValidation({ height: value })
                }
                required
              />
            </div>
            <FormInput
              value={wall.isShaded ? 'Yes' : 'No'}
              label='Shaded Wall'
              type='select'
              options={['Yes', 'No']}
              onChange={(value) =>
                handleUpdateWithValidation({ isShaded: value === 'Yes' })
              }
              required
              preventDeselection
            />
            <FormInput
              value={wall.wallType}
              label='Wall Type'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                wall,
                'wallType'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  wallType: value,
                  blockCore: value !== 'Frame' ? 'Open' : undefined,
                  studMaterial: value !== 'Block' ? 'Wood' : undefined,
                  cavityInsulation: 'None'
                });
              }}
              required
              preventDeselection
            />
            {wall.wallType !== 'Block' && (
              <FormInput
                value={wall.cavityInsulation}
                label='Cavity Insulation'
                type='select'
                options={getValidSizingAttributeOptions(
                  sizingItemType,
                  wall,
                  'cavityInsulation'
                )}
                onChange={(value) => {
                  handleUpdateWithValidation({
                    cavityInsulation: value
                  });
                }}
                required
                preventDeselection
              />
            )}
            <FormInput
              value={wall.boardInsulation}
              label='Board Insulation'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                wall,
                'boardInsulation'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  boardInsulation: value
                });
              }}
              required
              preventDeselection
            />
            <FormInput
              value={wall.exteriorFinish}
              label='Exterior Finish'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                wall,
                'exteriorFinish'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  exteriorFinish: value
                });
              }}
              required
              preventDeselection
            />
            <FormInput
              value={wall.partitionUnconditionedType}
              label='Adjacent To'
              type='select'
              options={[
                'Garage Under Vented Attic',
                'Garage with Conditioned Space Above',
                'Sunroom with Double Pane Clear Glass',
                'Sunroom with Spectrally Selective Glass'
              ]}
              onChange={(value) => {
                handleUpdateWithValidation({
                  partitionUnconditionedType: value
                });
              }}
            />
            {isModified && (
              <div className='flex w-full justify-end'>
                <button
                  className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                  type='button'
                  onClick={setAsDefault}
                >
                  <span>Set as Default</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <WallFenestrations
        wallType='Above Grade'
        wall={wall}
        updateFn={updateFn}
        updateDefaultSizingFn={updateDefaultSizingFn}
        focusFenestrationId={focusFenestrationId}
      />
    </div>
  );
}
